import { DEV } from 'cc/env';
import { CCInteger, Node, _decorator, Layout, CCFloat, warn, Font, random, Label } from 'cc';
import RollingNumberUnit from './RollingNumberUnit';
import Delay from '../../../Utils/Delay';
import NumberFormatter, { NumberFormatMode } from '../../Label/NumberFormatter';
import Game from '../../../../Game';
import GameDependency from '../../../../System/GameDependency';
import Account from '../../../../Account';

const { ccclass, property } = _decorator;

enum EventType {
    START = 'start',
    DIGIT_CHANGED = 'digit-changed',
    STOP = 'stop',
    INTERRUPT = 'interrupt',
}

const skipDisplayThousandCommaFormats = [NumberFormatMode.EmptyComma, NumberFormatMode.EmptyDot, NumberFormatMode.EmptySpace];

@ccclass('RollingNumber')
export default class RollingNumber extends GameDependency {
    public static readonly EVENT_TYPE = EventType;

    @property({ min: 0, max: 10, tooltip: DEV && '限制 fast mode 數字最慢轉動速度，若低於限制則使用此設定速度' })
    public readonly fastModeSpeedMin: number = 4;
    @property({ min: 1, tooltip: DEV && '限制 fast mode 數字最快轉動速度，若超過限制則使用此設定速度' })
    public readonly fastModeSpeedMax: number = 7;
    @property({ min: 5, tooltip: DEV && '數字轉動速度若高於設定，則使用 fast mode font' })
    public readonly blurThreshold: number = 7;
    @property({ tooltip: DEV && '是否在 Fast Mode 使用隨機數字' })
    public readonly useRandomNumber: boolean = false;
    @property(Font)
    public readonly normalFont: Font = null;
    @property(Font)
    public readonly fastModeFont: Font = null;
    @property({ type: CCFloat, tooltip: DEV && '滾動表演的時長 (單位: 毫秒)' })
    private readonly rollingDuration: number = 5000;
    @property({ type: CCInteger, tooltip: DEV && '乘上多少做表演, 避免小數點表演出問題\n(代表需設定預期會顯示到小數第幾位)' })
    private readonly decimalRate: number = 1;
    @property({ tooltip: DEV && '是否顯示0' })
    private readonly displayZero: boolean = true;
    @property({ tooltip: DEV && '是否顯示無用的0' })
    private readonly displayLeadingZero: boolean = true;
    @property({ type: [Node], tooltip: DEV && '控制千分位逗號, 需從低位到高位依序設定' })
    private readonly commaNodes: Node[] = [];
    @property({ tooltip: DEV && '是否啟用 NumberFormat 會依照網址參數設定千分位及小數點的符號' })
    private readonly useNumberFormat: boolean = false;
    @property({
        type: Node,
        tooltip: DEV && '小數點',
        visible(this: RollingNumber) {
            return this.useNumberFormat;
        },
    })
    private readonly dotNode: Node = null;
    @property({ tooltip: DEV && '是否啟用 Unit Sign 會依照幣別設定符號' })
    private readonly useCurrencySign: boolean = false;
    @property({
        tooltip: DEV && '是否使用單字元貨幣符號',
        visible(this: RollingNumber) {
            return this.useCurrencySign;
        },
    })
    private readonly useSingleCharSign: boolean = false;
    // eslint-disable-next-line @typescript-eslint/member-ordering
    @property({
        type: Node,
        tooltip: DEV && '符號',
        visible(this: RollingNumber) {
            return this.useCurrencySign;
        },
    })
    public readonly signNode: Node = null;
    @property({ tooltip: DEV && '當新的滾分跟上一次相同時, 不特別重設滾動' })
    private readonly skipSameRolling: boolean = false;
    @property({ tooltip: DEV && '當新的滾分數值較低時, 直接用重設的' })
    private readonly skipRollingOnDecrease: boolean = false;
    @property({ tooltip: DEV && '是否受 timescale 影響' })
    private readonly isInfluencedByTimeScale: boolean = true;

    public baseRollingDuration: number = 0;

    private _units: RollingNumberUnit[] = [];
    private _originValue: number = 0;
    private _targetValue: number = 0;
    private _startTime: number = 0;
    private _targetTime: number = 0;
    private _lastFrameTime: number = 0;

    private _lastSpeedList: number[] = [];
    private _currentSpeedList: number[] = [];
    // 原本預計可以透過Config設定切換
    private _isSlowMode: boolean = false;
    private _baseUnitIndex: number = 0;

    public get unitLength() {
        return this._units.length;
    }

    public get baseSpeed() {
        return this._currentSpeedList[0] ?? 0;
    }

    public get lastBaseSpeed() {
        return this._lastSpeedList[0] ?? 0;
    }

    public get currentSpeedList() {
        return this._currentSpeedList;
    }

    public get previousSpeedList() {
        return this._lastSpeedList;
    }

    public get isSlowMode() {
        return this._isSlowMode;
    }

    public get currentValue() {
        return this.convertToDecimal([...this._units].reverse().reduce((acc, unit) => acc * 10 + unit.currentValue, 0));
    }

    public set currentValue(value: number) {
        this._originValue = this.convertToInterger(value);
        this._targetValue = this._originValue;
        const unitValues = this._getUnitByValue(this._targetValue);
        for (let i = 0; i < this._currentSpeedList.length; i++) {
            this._currentSpeedList[i] = 0;
        }
        for (let i = this._units.length - 1; i >= 0; i--) {
            const unit = this._units[i];
            unit.currentDigitOffset = i;
            unit.skipRollingOnDecrease(unitValues[i]);
            unit.updateFont();
        }
        this._resetAllCarrying();
        this._resetUnitNodeActive();
        this.node.emit(EventType.DIGIT_CHANGED);
    }

    // for tester
    public get lastTargetValue() {
        return this.convertToDecimal(this._targetValue);
    }

    private get units() {
        if (!this._units.length) {
            this._units = this.getComponentsInChildren(RollingNumberUnit);
        }
        return this._units;
    }

    /**
     * 將浮點數轉換成整數 ex. 1.2345 -> 12345
     * @param value 浮點數
     * @returns 整數
     */
    public convertToInterger(value: number) {
        return Math.floor(Number((value * 10 ** this.decimalRate).toFixed(this.decimalRate)));
    }

    /**
     * 將整數轉換成浮點數 ex. 12345 -> 1.2345
     * @param value 整數
     * @returns 浮點數
     */
    public convertToDecimal(value: number) {
        return value / 10 ** this.decimalRate;
    }

    /**
     * 滾動滾輪
     * @param targetValue 本次滾動的目標數值
     */
    public async startRolling(targetValue: number, rollingDuration: number = this.rollingDuration) {
        if (this.skipSameRolling && this.convertToInterger(targetValue) === this._targetValue) {
            return;
        }
        if (this.skipRollingOnDecrease && this.convertToInterger(targetValue) < this._targetValue) {
            this.currentSpeedList.forEach((num, index) => {
                this.currentSpeedList[index] = 0;
            });
            this.units.forEach((unit, index) => {
                unit.currentDigitOffset = index;
            });
            // 必須先 emit interrupt，因設置 currentValue 會直接觸發 RESET_POSITION
            this.emit(RollingNumber.EVENT_TYPE.INTERRUPT);
            this.currentValue = targetValue;
            return;
        }
        if (rollingDuration <= 0) {
            warn('rollingDuration should be greater than 0');
            return;
        }

        if (this.enabled && this._startTime !== 0 && this._targetTime !== 0) {
            const result = await Promise.race([
                Delay.event(RollingNumberUnit.EVENT_TYPE.RESET_POSITION, this.units[0]),
                Delay.event(RollingNumber.EVENT_TYPE.INTERRUPT, this),
            ]);
            if (result === RollingNumber.EVENT_TYPE.INTERRUPT) {
                return;
            }
            const timeProgress = (Date.now() - this._startTime) / (this._targetTime - this._startTime);
            const valueDiff = this._targetValue - this._originValue;
            const value = this._originValue + (this._targetValue - this._originValue) * timeProgress;
            this._originValue = valueDiff > 0 ? Math.ceil(value) : Math.floor(value);
        } else {
            this._originValue = this.convertToInterger(this.currentValue);
        }

        this.enabled = true;
        this.node.emit(EventType.DIGIT_CHANGED);

        this._targetValue = this.convertToInterger(targetValue);
        // 原本 targetValue 和 originValue 相同還是會更新 targetTime ，導致會有一段時間無法更新 RollingNumber
        if (this._targetValue !== this._originValue) {
            this._startTime = Date.now();
            this._lastFrameTime = this._startTime;
            this._targetTime = this._startTime + rollingDuration;
            this.baseRollingDuration = rollingDuration / 1000;
            this._updateSpeed();
        }
    }

    public async stop(resetToTargetValue: boolean = true) {
        if (resetToTargetValue) {
            this.currentValue = this.convertToDecimal(this._targetValue);
            // prevent carry in maximum digit unit is 0
            this.currentValue = this.convertToDecimal(this._targetValue);
        }
        this.enabled = false;
    }

    protected onLoad() {
        if (this.getComponent(Layout)?.horizontalDirection === Layout.HorizontalDirection.LEFT_TO_RIGHT) {
            this.units.reverse();
        }
        this.units?.forEach((unit, index) => {
            unit.init(this, index, this.units[index + 1], this.units[index - 1]);
        });
        this.on(EventType.DIGIT_CHANGED, () => {
            const value = this.convertToInterger(this.currentValue);
            this._updateCommaNodeVisibility(value);
            // this._resetUnitNodeActive(value);
        });
        this._resetUnitNodeActive();
        this._updateCommaNodeVisibility(this.currentValue);

        if (this.useNumberFormat) {
            if (this.dotNode) {
                const dotLabel = this.dotNode.getComponent(Label) ?? this.dotNode.getComponentInChildren(Label);
                dotLabel.string = dotLabel.string.replace(/./g, NumberFormatter.numberFormat.decimal);
            }
            this.commaNodes.forEach((node) => {
                const commaLabel = node.getComponent(Label) ?? node.getComponentInChildren(Label);
                if (commaLabel) {
                    commaLabel.string = commaLabel.string.replace(/./g, NumberFormatter.numberFormat.thousands);
                }
            });
        }

        if (this.useCurrencySign) {
            if (this.signNode) {
                this._updateCurrencySign();
                Game.account.on(Account.EVENT_TYPE.CURRENCY_CHANGED, this._updateCurrencySign, this);
            }
        }
    }

    protected update(deltaTime: number) {
        const currentTime = Date.now();
        if (this._startTime === 0 || this._targetTime === 0) {
            this.enabled = false;
            return;
        }
        if (currentTime >= this._targetTime) {
            this.stop();
            return;
        }
        // 當前進度
        const timeProgress = (currentTime - this._startTime) / (this._targetTime - this._startTime);
        // 當前值
        const value = this._originValue + (this._targetValue - this._originValue) * timeProgress;
        // 位數多一階
        const digitChanged = (this.currentValue * 10 ** this.decimalRate).toString().length !== Math.floor(value).toString().length;

        const timeDiff = (currentTime - this._lastFrameTime) / 1000;
        const dt = this.isInfluencedByTimeScale ? deltaTime : timeDiff;
        for (let i = this.units.length - 1; i >= 0; i--) {
            const unit = this.units[i];
            unit.updateRollingProgress(dt);
        }
        if (digitChanged) {
            this.node.emit(EventType.DIGIT_CHANGED);
        }
        this._lastFrameTime = currentTime;
    }

    private _resetAllCarrying() {
        this.units[0].resetCarrying();
    }

    private _getCurrentRollingValue() {
        const targetValueUnits = this._getUnitByValue(this._originValue);
        let rollingValue = '';
        targetValueUnits.reverse().forEach((value, index) => {
            let valueUnit = value;
            const unit = this.units[this.units.length - 1 - index];
            if (unit.isGearMode || unit.isBaseUnit) {
                valueUnit = unit.currentValue;
            }
            rollingValue += valueUnit;
        });
        return +rollingValue;
    }

    private async _updateSpeed() {
        let awaitTime = 0;
        const baseUnit = this.units[this._baseUnitIndex];
        if (baseUnit) {
            if (baseUnit.node.y !== 0 && baseUnit.currentSpeed !== 0) {
                // 多等 BaseUnit 跑完會導致 originValue 的 BaseUnit 有機會多跑 1
                // 使 rollingValue 後續設定 FastModeValue 時會超過原本數字，導致比預期的超前
                // 若此時大於 target 則衍生出倒轉問題
                const tempRollingValue = this._getCurrentRollingValue();
                const startAwaitTime = Date.now();
                await Delay.event(RollingNumberUnit.EVENT_TYPE.RESET_POSITION, this.units[this._baseUnitIndex]);
                if (tempRollingValue + 10 ** this._baseUnitIndex >= this._targetValue) {
                    this.currentValue = this._targetValue;
                    return;
                }
                awaitTime = (Date.now() - startAwaitTime) / 1000;
            }
        }
        const rollingValue = this._getCurrentRollingValue();
        const valueDiff = this._targetValue - rollingValue;
        // 速度設定
        const totalRollingTime = this.baseRollingDuration - awaitTime;
        const valuePerSecond = valueDiff / totalRollingTime;
        const baseDigitIndex = Math.max(0, Math.abs(valuePerSecond).toString().split('.')[0].length - 1);
        const baseSpeed = (valueDiff > 0 ? Math.floor(valueDiff / 10 ** baseDigitIndex) : Math.ceil(valueDiff / 10 ** baseDigitIndex)) / totalRollingTime;
        // unit 設定
        this._baseUnitIndex = baseDigitIndex;
        const lastOriginValueUnit = this._getUnitByValue(this._originValue);
        const speedList = [];
        const additionalSpeedMultiple = Math.abs(baseSpeed) < this.fastModeSpeedMin / 2 ? 10 : 1;
        this.units.forEach((unit, index) => {
            const digitOffset =
                Math.abs(index - baseDigitIndex) >= lastOriginValueUnit.length
                    ? (lastOriginValueUnit.length - 1) * Math.sign(index - baseDigitIndex)
                    : index - baseDigitIndex;
            unit.setFastModeUnitValue(lastOriginValueUnit[index], digitOffset);
            // 設定 unit 對於基準的 Offset
            unit.currentDigitOffset = digitOffset;

            // 設定 unit 速度列表
            if (baseDigitIndex > index) {
                const speed = baseSpeed * (Math.abs(digitOffset) + 1) * additionalSpeedMultiple;
                if (Math.abs(speed) >= this.fastModeSpeedMax) {
                    // speedList.push((this.fastModeSpeedMax) * (speed > 0 ? 1 : -1) * (1 - digitOffset / 10));
                    speedList.push((this.fastModeSpeedMax + Math.abs(digitOffset + 1) + random()) * (speed > 0 ? 1 : -1));
                } else {
                    speedList.push(speed);
                }
            } else if (baseDigitIndex === index) {
                speedList.push(baseSpeed);
            }
        });
        this._lastSpeedList = this._currentSpeedList;
        this._currentSpeedList = speedList.reverse();
        this.units[baseDigitIndex]?.checkCarryStatus();
        this.units.forEach((unit) => {
            unit.updateFont(baseSpeed <= 0);
        });
        if (baseSpeed <= 0) {
            this._resetAllCarrying();
        }
        this._resetUnitNodeActive();
    }

    /**
     * 檢查是否應該顯示沒有用的0 ex. 0123 中前面的0
     * @param index 數字在units中的index
     */
    private _shouldDisplayLeadingZero(index: number) {
        return this.displayLeadingZero || (this.displayZero && index <= this.decimalRate);
    }

    /**
     * 從給定的值中取得每個單位數值的陣列
     */
    private _getUnitByValue(value: number): number[] {
        return this.units.map((unit, i) => Math.floor((value % 10 ** (i + 1)) / 10 ** i));
    }

    /**
     * 更新千分位逗號是否顯示
     * @param value 要顯示的數值
     */
    private _updateCommaNodeVisibility(value: number) {
        if (!this.displayLeadingZero) {
            const needDisplayComma = !skipDisplayThousandCommaFormats.includes(this.game.config.numberFormatterMode);
            this.commaNodes.forEach((node, index) => {
                const limit = 10 ** ((index + 1) * 3 + this.decimalRate);
                node.active = value >= limit && needDisplayComma;
            });
        }
    }

    private _resetUnitNodeActive() {
        // const targetUnitValues = this._units.map((unit, i) => Math.floor((value % 10 ** (i + 1)) / 10 ** i));
        let hasValue = false;
        const currentValueUnit = this._getUnitByValue(this.currentValue * 10 ** this.decimalRate);
        for (let i = this.units.length - 1; i >= 0; i--) {
            if (!hasValue && currentValueUnit[i] === 0) {
                this.units[i].node.active = this._shouldDisplayLeadingZero(i);
            } else {
                hasValue = true;
                this.units[i].node.active = true;
            }
        }
    }

    private _updateCurrencySign() {
        if (!this.signNode) {
            return;
        }
        const signLabel = this.signNode.getComponent(Label) ?? this.signNode.getComponentInChildren(Label);
        const sign = this.useSingleCharSign ? Game.account.singleCharCurrencySign : Game.account.sign;
        signLabel.string = sign;
        this.signNode.active = !Game.config.hideCurrency;
    }
}
